import type { ReactNode } from 'react'

import { VisuallyHidden, cn } from '@aeris/ui'

/**
 * Os gráficos do painel (F134), sem biblioteca.
 *
 * O projeto não tem biblioteca de gráfico, e o desktop não carrega nada de CDN:
 * barras em CSS resolvem o que o dono pergunta — "que hora vendeu mais?",
 * "quem vendeu mais?" — sem trazer dezenas de kB para desenhar retângulos.
 *
 * Cada gráfico leva a TABELA equivalente para leitor de tela: uma barra é um
 * desenho, e o número que ela representa precisa existir em texto.
 *
 * As alturas usam `Number` porque são pixels, não dinheiro: a conta de dinheiro
 * já foi feita no servidor, e aqui só se decide o tamanho de um retângulo.
 */

export type ColumnPoint = {
  readonly key: string
  /** O rótulo curto embaixo da coluna: "14h", "09/09". */
  readonly label: string
  /** Para a altura. */
  readonly value: number
  /** O que se lê: "R$ 350,00 · 3 vendas". */
  readonly description: string
}

export const ColumnChart = ({
  points,
  caption,
}: {
  points: readonly ColumnPoint[]
  caption: string
}): ReactNode => {
  const highest = Math.max(0, ...points.map((point) => point.value))
  // Com muitas colunas, só alguns rótulos cabem: um a cada N.
  const every = Math.max(1, Math.ceil(points.length / 12))

  return (
    <figure className="flex flex-col gap-1">
      <div
        aria-hidden="true"
        className="flex h-32 items-end gap-px border-b border-(--border-subtle)"
      >
        {points.map((point) => (
          <div
            key={point.key}
            title={`${point.label}: ${point.description}`}
            className="flex h-full min-w-0 flex-1 items-end"
          >
            <div
              className={cn(
                'w-full rounded-t-(--radius-sm)',
                point.value > 0
                  ? 'bg-(--brand-primary)'
                  : 'bg-(--border-subtle)'
              )}
              style={{
                height:
                  highest === 0 || point.value <= 0
                    ? '2px'
                    : `${String(Math.max(4, (point.value / highest) * 100))}%`,
              }}
            />
          </div>
        ))}
      </div>
      <div aria-hidden="true" className="flex gap-px">
        {points.map((point, index) => (
          <span
            key={point.key}
            className="min-w-0 flex-1 truncate text-center text-(length:--text-xs) text-(--text-muted)"
          >
            {index % every === 0 ? point.label : ''}
          </span>
        ))}
      </div>
      <VisuallyHidden>
        <table>
          <caption>{caption}</caption>
          <tbody>
            {points.map((point) => (
              <tr key={point.key}>
                <th scope="row">{point.label}</th>
                <td>{point.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
      <figcaption className="sr-only">{caption}</figcaption>
    </figure>
  )
}

/** Uma barra horizontal proporcional, para dentro de uma célula ou lista. */
export const ShareBar = ({
  value,
  highest,
  tone = 'brand',
}: {
  value: number
  highest: number
  tone?: 'brand' | 'warning' | 'danger'
}): ReactNode => (
  <div
    aria-hidden="true"
    className="h-1.5 w-full overflow-hidden rounded-full bg-(--border-subtle)"
  >
    <div
      className={cn(
        'h-full rounded-full',
        tone === 'brand'
          ? 'bg-(--brand-primary)'
          : tone === 'warning'
            ? 'bg-(--warning)'
            : 'bg-(--danger)'
      )}
      style={{
        width:
          highest <= 0 || value <= 0
            ? '0%'
            : `${String(Math.min(100, (value / highest) * 100))}%`,
      }}
    />
  </div>
)
